import type { Theme } from "@mui/material"
import {
  Divider,
  Typography,
  DialogActions,
  DialogTitle,
  DialogContent,
  Dialog,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  useTheme,
  Stack,
  useMediaQuery,
} from "@mui/material"
import { useCallback, useEffect, useContext, useState } from "react"
import type { IFeatureStyled, IProject } from "../../interfaces"
import CloseIcon from "@mui/icons-material/Close"
import SettingsContext from "../../contexts/settings.context"
import { featureService, projectService, surveyService, worldService } from "../../services"
import { UPDATE_SETTINGS } from "../../contexts/reducers/settings.reducer"
import LoadingButton from "@mui/lab/LoadingButton"

interface MoveWorldModalProps {
  openMoveModal: boolean
  setOpenMoveModal: (open: boolean) => void
  feature: IFeatureStyled | undefined
}

const MoveWorldModal = ({ openMoveModal, setOpenMoveModal, feature }: MoveWorldModalProps) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"))
  const { settingsState, updateSettings } = useContext(SettingsContext)
  const [worlds, setWorlds] = useState<any[]>([])
  const [selectedWorld, setSelectedWorld] = useState<string>("")
  const [selectedProject, setSelectedProject] = useState<string>("")
  const [selectedJob, setSelectedJob] = useState<string>("")
  const [projects, setProjects] = useState<IProject[]>([])
  const [jobs, setJobs] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    setSelectedWorld("")
    setSelectedProject("")
    setSelectedJob("")
  }, [openMoveModal])

  const fetchWorlds = useCallback(async () => {
    if (settingsState.worlds.length > 0) {
      setWorlds(settingsState.worlds)
    } else {
      const worlds = await worldService.getWorlds()
      if (worlds) {
        setWorlds(worlds)
      }
    }
  }, [selectedWorld])

  const fetchProjects = useCallback(async () => {
    if (selectedWorld) {
      try {
        const res = await projectService.getAll(Number(selectedWorld))
        if (res) {
          setProjects(res)
        }
      } catch (error) {
        console.log(error)
      }
    }
  }, [selectedWorld])

  const fetchSurveys = useCallback(async () => {
    const surveys = await surveyService.search("all", Number(selectedProject) ?? 0, "", -1, "in progress")
    if (surveys) {
      setJobs(surveys)
    }
  }, [selectedProject])

  useEffect(() => {
    fetchWorlds()
  }, [fetchWorlds])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  useEffect(() => {
    fetchSurveys()
  }, [fetchSurveys])

  const handleSave = async () => {
    setIsLoading(true)
    const response = await featureService.moveFeature(feature?.featureId ?? 0, Number(selectedJob))
    if (response.status === 200) {
      updateSettings(UPDATE_SETTINGS, {
        ...settingsState,
        activeFeature: undefined,
        featuresFilter: {
          ...settingsState.featuresFilter,
          featureMoved: true,
        },
      })
      setOpenMoveModal(false)
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={openMoveModal} onClose={() => setOpenMoveModal(false)} sx={{ p: 1 }}>
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        {`Move Feature`}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setOpenMoveModal(false)}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Typography sx={{ mb: 2 }}>
          Select the world, project, and job that you would like to move the feature to.
        </Typography>
        <Stack direction={"column"} spacing={1} justifyContent="space-between">
          <FormControl size="small" sx={{ p: 0, m: 0, minWidth: 120 }} variant="outlined">
            <InputLabel id="form-status-label">World</InputLabel>
            <Select
              size="small"
              labelId="form-status-label"
              id="form-status-select"
              label="World"
              value={selectedWorld}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: isDesktop ? "75vh" : "50vh",
                    overflow: "auto",
                  },
                },
              }}
              sx={{
                borderRadius: 3,
                borderColor: theme.palette.grey[200],
                backgroundColor: "white",
                height: 44,
                "& .MuiSelect-select": {
                  fontSize: 14,
                },
              }}
              onChange={(event) => {
                setSelectedWorld(event.target.value)
                if (event.target.value !== selectedWorld) {
                  setSelectedProject("")
                  setSelectedJob("")
                }
              }}>
              {worlds
                .sort((a, b) => a.worldName?.localeCompare(b.worldName ?? "") ?? 0)
                .map((world, index) => {
                  return (
                    <MenuItem key={index} value={world.worldId}>
                      {world.worldName}
                    </MenuItem>
                  )
                })}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem />
          <FormControl size="small" sx={{ p: 0, m: 0, minWidth: 120 }} variant="outlined">
            <InputLabel id="form-status-label">Project</InputLabel>
            <Select
              disabled={!selectedWorld}
              size="small"
              labelId="form-status-label"
              id="form-status-select"
              label="Project"
              value={selectedProject}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: isDesktop ? "75vh" : "50vh",
                    overflow: "auto",
                  },
                },
              }}
              sx={{
                borderRadius: 3,
                borderColor: theme.palette.grey[200],
                backgroundColor: "white",
                height: 44,
                "& .MuiSelect-select": {
                  fontSize: 14,
                },
              }}
              onChange={(event) => {
                setSelectedProject(event.target.value)
                if (event.target.value !== selectedProject) {
                  setSelectedJob("")
                }
              }}>
              {projects.length > 0 && projects ? (
                projects
                  .sort((a, b) => a.projectName?.localeCompare(b.projectName ?? "") ?? 0)
                  .map((proj, index) => {
                    return (
                      <MenuItem key={index} value={proj.projectId}>
                        {proj.projectName}
                      </MenuItem>
                    )
                  })
              ) : (
                <MenuItem value="">No projects found</MenuItem>
              )}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem />
          <FormControl size="small" sx={{ p: 0, m: 0, minWidth: 120 }} variant="outlined">
            <InputLabel id="form-status-label">Job</InputLabel>
            <Select
              disabled={!selectedProject}
              size="small"
              labelId="form-status-label"
              id="form-status-select"
              label="Job"
              value={selectedJob}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: "50vh",
                    overflow: "auto",
                  },
                },
              }}
              sx={{
                borderRadius: 3,
                borderColor: theme.palette.grey[200],
                backgroundColor: "white",
                height: 44,
                "& .MuiSelect-select": {
                  fontSize: 14,
                },
              }}
              onChange={(event) => {
                setSelectedJob(event.target.value)
              }}>
              {jobs.length > 0 && jobs ? (
                jobs
                  .sort((a, b) => a.surveyName?.localeCompare(b.surveyName ?? "") ?? 0)
                  .map((job, index) => {
                    return (
                      <MenuItem key={index} value={job.surveyId}>
                        {job.surveyName}
                      </MenuItem>
                    )
                  })
              ) : (
                <MenuItem value="">No jobs found</MenuItem>
              )}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          disabled={!selectedJob}
          loading={isLoading}
          variant="outlined"
          autoFocus
          onClick={handleSave}
          sx={{ textTransform: "capitalize" }}>
          Save changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default MoveWorldModal
